import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { usePermission } from "@/hooks/usePermission";
import { applicationService } from "@/services/applicationService";
import { userService } from "@/services/userService";
import { roleService } from "@/services/roleService";
import { auditLogService } from "@/services/auditLogService";
import {
  Layers,
  Users,
  ShieldCheck,
  FileText,
  RefreshCw,
  ArrowRight,
  Shield,
  KeyRound,
  Building2,
} from "lucide-react";
import { Button } from "@/components/common/Button";
import { Badge } from "@/components/common/Badge";
import { NavTab } from "@/components/common/Sidebar";

interface DashboardOverviewProps {
  onNavigate: (tab: NavTab) => void;
}

export const DashboardOverview: React.FC<DashboardOverviewProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const { hasPermission } = usePermission();

  const [stats, setStats] = useState({
    applications: 0,
    users: 0,
    roles: 0,
    auditLogs: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [appsRes, usersRes, rolesRes, auditRes] = await Promise.allSettled([
        hasPermission("applications:list") ? applicationService.list(1, 10) : null,
        hasPermission("users:list") ? userService.list(1, 10) : null,
        hasPermission("roles:list") ? roleService.listRoles() : null,
        hasPermission("audit:list") ? auditLogService.list({ page: 1, limit: 1 }) : null,
      ]);

      const appsCount =
        appsRes.status === "fulfilled" && appsRes.value
          ? Array.isArray(appsRes.value)
            ? appsRes.value.length
            : appsRes.value.total || 0
          : 0;

      const usersCount =
        usersRes.status === "fulfilled" && usersRes.value
          ? Array.isArray(usersRes.value)
            ? usersRes.value.length
            : usersRes.value.total || 0
          : 0;

      const rolesCount =
        rolesRes.status === "fulfilled" && rolesRes.value && Array.isArray(rolesRes.value)
          ? rolesRes.value.length
          : 0;

      let logsCount = 0;
      if (auditRes.status === "fulfilled" && auditRes.value) {
        if (Array.isArray(auditRes.value)) {
          logsCount = auditRes.value.length;
        } else {
          logsCount = auditRes.value.total || 0;
        }
      }

      setStats({
        applications: appsCount,
        users: usersCount,
        roles: rolesCount,
        auditLogs: logsCount,
      });
    } finally {
      setIsLoading(false);
    }
  }, [hasPermission]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="space-y-6">
      {/* Welcome banner */}
      <div className="card dashboard-welcome-card">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900">
                Olá, {user?.name || "Administrador"}!
              </h1>
              <Badge variant="info" className="text-xs font-mono">
                {user?.roles?.[0] || "ADMIN"}
              </Badge>
            </div>
            <p className="text-muted mt-1 text-sm">
              Painel de Governança e Administração Central da <strong>Auth API</strong>.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={loadData}
              isLoading={isLoading}
              title="Recarregar métricas operacionais"
            >
              <RefreshCw size={15} />
              <span>Atualizar</span>
            </Button>
          </div>
        </div>

        {/* Dynamic operational indicators (real data from session/user) */}
        <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-gray-100 text-xs text-muted">
          <span className="flex items-center gap-1.5">
            <Building2 size={13} className="text-primary" />
            <span>Tenant Ativo:</span>
            <code className="text-gray-900 font-mono font-semibold">
              {user?.application_id || "auth-admin"}
            </code>
          </span>
          <span className="text-gray-300">|</span>
          <span className="flex items-center gap-1.5">
            <Shield size={13} className="text-purple-600" />
            <span>Perfis Vinculados:</span>
            <span className="text-gray-900 font-medium">{user?.roles?.join(", ") || "Nenhum"}</span>
          </span>
          <span className="text-gray-300">|</span>
          <span className="flex items-center gap-1.5">
            <KeyRound size={13} className="text-green-600" />
            <span>E-mail:</span>
            <span className="text-gray-900 font-medium">{user?.email}</span>
          </span>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {hasPermission("applications:list") && (
          <div
            className="card stat-card cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onNavigate("applications")}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted uppercase tracking-wider">
                  Aplicações
                </p>
                <h3 className="text-2xl font-bold mt-1 text-gray-900">{stats.applications}</h3>
                <p className="text-xs text-primary mt-1 font-medium flex items-center gap-1">
                  <span>Tenants Registrados</span>
                  <ArrowRight size={12} />
                </p>
              </div>
              <div className="stat-icon-wrapper bg-blue-50 text-blue-600">
                <Layers size={22} />
              </div>
            </div>
          </div>
        )}

        {hasPermission("users:list") && (
          <div
            className="card stat-card cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onNavigate("users")}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted uppercase tracking-wider">
                  Usuários
                </p>
                <h3 className="text-2xl font-bold mt-1 text-gray-900">{stats.users}</h3>
                <p className="text-xs text-green-600 mt-1 font-medium flex items-center gap-1">
                  <span>Contas Gerenciadas</span>
                  <ArrowRight size={12} />
                </p>
              </div>
              <div className="stat-icon-wrapper bg-green-50 text-green-600">
                <Users size={22} />
              </div>
            </div>
          </div>
        )}

        {hasPermission("roles:list") && (
          <div
            className="card stat-card cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onNavigate("roles")}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted uppercase tracking-wider">
                  Roles & RBAC
                </p>
                <h3 className="text-2xl font-bold mt-1 text-gray-900">{stats.roles}</h3>
                <p className="text-xs text-purple-600 mt-1 font-medium flex items-center gap-1">
                  <span>Perfis de Acesso</span>
                  <ArrowRight size={12} />
                </p>
              </div>
              <div className="stat-icon-wrapper bg-purple-50 text-purple-600">
                <ShieldCheck size={22} />
              </div>
            </div>
          </div>
        )}

        {hasPermission("audit:list") && (
          <div
            className="card stat-card cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => onNavigate("audit")}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-muted uppercase tracking-wider">
                  Auditoria
                </p>
                <h3 className="text-2xl font-bold mt-1 text-gray-900">{stats.auditLogs}</h3>
                <p className="text-xs text-amber-600 mt-1 font-medium flex items-center gap-1">
                  <span>Eventos Registrados</span>
                  <ArrowRight size={12} />
                </p>
              </div>
              <div className="stat-icon-wrapper bg-amber-50 text-amber-600">
                <FileText size={22} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions / Navigation Cards */}
      <div>
        <h2 className="text-base font-bold text-gray-900 mb-3">Módulos Administrativos</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {hasPermission("applications:list") && (
            <button
              type="button"
              onClick={() => onNavigate("applications")}
              className="p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-blue-300 hover:shadow-xs transition-all flex flex-col justify-between"
            >
              <div>
                <div className="p-2 rounded bg-blue-50 text-blue-600 w-fit mb-3">
                  <Layers size={18} />
                </div>
                <h3 className="font-semibold text-sm text-gray-900">Aplicações & Clientes OAuth</h3>
                <p className="text-xs text-muted mt-1 leading-relaxed">
                  Gerencie clientes OAuth2, domínios autorizados e tempos de expiração de token.
                </p>
              </div>
              <span className="text-xs text-primary font-medium mt-3 flex items-center gap-1">
                Acessar módulo <ArrowRight size={12} />
              </span>
            </button>
          )}

          {hasPermission("users:list") && (
            <button
              type="button"
              onClick={() => onNavigate("users")}
              className="p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-green-300 hover:shadow-xs transition-all flex flex-col justify-between"
            >
              <div>
                <div className="p-2 rounded bg-green-50 text-green-600 w-fit mb-3">
                  <Users size={18} />
                </div>
                <h3 className="font-semibold text-sm text-gray-900">Gestão de Identidades</h3>
                <p className="text-xs text-muted mt-1 leading-relaxed">
                  Cadastre novos usuários, vincule perfis e controle status de verificação.
                </p>
              </div>
              <span className="text-xs text-primary font-medium mt-3 flex items-center gap-1">
                Acessar módulo <ArrowRight size={12} />
              </span>
            </button>
          )}

          {hasPermission("roles:list") && (
            <button
              type="button"
              onClick={() => onNavigate("roles")}
              className="p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-purple-300 hover:shadow-xs transition-all flex flex-col justify-between"
            >
              <div>
                <div className="p-2 rounded bg-purple-50 text-purple-600 w-fit mb-3">
                  <ShieldCheck size={18} />
                </div>
                <h3 className="font-semibold text-sm text-gray-900">Perfis & Permissões RBAC</h3>
                <p className="text-xs text-muted mt-1 leading-relaxed">
                  Configure papéis e vincule ações granulares do catálogo canônico operacional.
                </p>
              </div>
              <span className="text-xs text-primary font-medium mt-3 flex items-center gap-1">
                Acessar módulo <ArrowRight size={12} />
              </span>
            </button>
          )}

          {hasPermission("audit:list") && (
            <button
              type="button"
              onClick={() => onNavigate("audit")}
              className="p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-amber-300 hover:shadow-xs transition-all flex flex-col justify-between"
            >
              <div>
                <div className="p-2 rounded bg-amber-50 text-amber-600 w-fit mb-3">
                  <FileText size={18} />
                </div>
                <h3 className="font-semibold text-sm text-gray-900">Trilha de Auditoria</h3>
                <p className="text-xs text-muted mt-1 leading-relaxed">
                  Inspecione o log imutável de eventos de segurança, payload e metadados JSON.
                </p>
              </div>
              <span className="text-xs text-primary font-medium mt-3 flex items-center gap-1">
                Acessar módulo <ArrowRight size={12} />
              </span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
